import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AiOutlineClose, AiOutlineLeft, AiOutlineRight } from 'react-icons/ai';
import { cx, useOverlay } from './hooks';

/** Full-screen image viewer. */
export const Lightbox = ({ images, index, onIndexChange, onClose }) => {
  const panelRef = useRef(null);
  const open = index !== null && index !== undefined;
  useOverlay(open, onClose, panelRef);

  useEffect(() => {
    if (!open || images.length < 2) return undefined;
    const onKey = e => {
      if (e.key === 'ArrowRight') onIndexChange((index + 1) % images.length);
      if (e.key === 'ArrowLeft') onIndexChange((index - 1 + images.length) % images.length);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, index, images.length, onIndexChange]);

  if (!open) return null;
  const image = images[index];

  return createPortal(
    <div
      ref={panelRef}
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
      tabIndex={-1}
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <img src={image?.src} alt={image?.alt || ''} />
      <button type="button" className="lightbox-btn is-close" onClick={onClose} aria-label="Close preview">
        <AiOutlineClose />
      </button>
      {images.length > 1 && (
        <>
          <span className="lightbox-count">
            {index + 1} / {images.length}
          </span>
          <button
            type="button"
            className="lightbox-btn is-prev"
            onClick={() => onIndexChange((index - 1 + images.length) % images.length)}
            aria-label="Previous image"
          >
            <AiOutlineLeft />
          </button>
          <button
            type="button"
            className="lightbox-btn is-next"
            onClick={() => onIndexChange((index + 1) % images.length)}
            aria-label="Next image"
          >
            <AiOutlineRight />
          </button>
        </>
      )}
    </div>,
    document.body
  );
};

const GroupContext = createContext(null);

/** Groups Images so the preview can move between them. */
export const ImagePreviewGroup = ({ children }) => {
  const entries = useRef([]);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [images, setImages] = useState([]);

  const register = useCallback((id, image) => {
    const list = entries.current.filter(e => e.id !== id);
    list.push({ id, ...image });
    entries.current = list;
    return () => {
      entries.current = entries.current.filter(e => e.id !== id);
    };
  }, []);

  const open = useCallback(id => {
    // Keep page order for navigation.
    const ordered = [...entries.current].sort((a, b) => {
      const ea = document.getElementById(a.id);
      const eb = document.getElementById(b.id);
      if (!ea || !eb) return 0;
      return ea.compareDocumentPosition(eb) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });
    setImages(ordered);
    setPreviewIndex(Math.max(0, ordered.findIndex(e => e.id === id)));
  }, []);

  return (
    <GroupContext.Provider value={{ register, open }}>
      {children}
      <Lightbox
        images={images}
        index={previewIndex}
        onIndexChange={setPreviewIndex}
        onClose={() => setPreviewIndex(null)}
      />
    </GroupContext.Provider>
  );
};

/**
 * <img> with an optional fallback image and click-to-preview.
 * width/height size the wrapper, className/style go on the <img>.
 * preview: true (default) | false | { src } to preview a different (e.g. larger) file.
 */
const Image = ({
  src,
  alt = '',
  width,
  height,
  className,
  style,
  rootClassName,
  fallback,
  preview = true,
  onError,
  onClick,
  loading = 'lazy',
}) => {
  const id = useId().replace(/:/g, '');
  const group = useContext(GroupContext);
  const [failed, setFailed] = useState(false);
  const [soloOpen, setSoloOpen] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  const shownSrc = failed && fallback ? fallback : src;
  const previewSrc = (preview && typeof preview === 'object' && preview.src) || shownSrc;
  const canPreview = preview !== false && !!shownSrc && !(failed && !fallback);

  useEffect(() => {
    if (!group || !canPreview) return undefined;
    return group.register(id, { src: previewSrc, alt });
  }, [group, id, canPreview, previewSrc, alt]);

  const handleClick = e => {
    onClick?.(e);
    if (!canPreview) return;
    if (group) group.open(id);
    else setSoloOpen(true);
  };

  const sizeStyle = {};
  if (width !== undefined) sizeStyle.width = width;
  if (height !== undefined) sizeStyle.height = height;

  return (
    <>
      <span
        id={id}
        className={cx('image', canPreview && 'is-previewable', rootClassName)}
        style={sizeStyle}
        onClick={handleClick}
        onKeyDown={e => {
          if (canPreview && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleClick(e);
          }
        }}
        role={canPreview ? 'button' : undefined}
        tabIndex={canPreview ? 0 : undefined}
        aria-label={canPreview ? `Preview ${alt || 'image'}` : undefined}
      >
        <img
          src={shownSrc}
          alt={alt}
          className={className}
          loading={loading}
          style={{ width: '100%', height: height !== undefined ? height : 'auto', ...style }}
          onError={e => {
            onError?.(e);
            if (!failed) setFailed(true);
          }}
        />
      </span>
      {!group && (
        <Lightbox
          images={[{ src: previewSrc, alt }]}
          index={soloOpen ? 0 : null}
          onIndexChange={() => {}}
          onClose={() => setSoloOpen(false)}
        />
      )}
    </>
  );
};

Image.PreviewGroup = ImagePreviewGroup;

export default Image;
